import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Checkbox,
  Spinner,
} from "@heroui/react";
import { ArrowLeft, Save, Image } from "lucide-react";

import { menuService } from "@/services/menuService";
import { Category } from "@/types/menu";
import ProductTypeManager from "@/components/menu/ProductTypeManager";

export default function CreateProductPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    price: "",
    category_ids: [] as number[],
    is_featured: false,
    unavailable: false,
    image_url: "",
  });

  // Created product ID (used for adding product types)
  const [productId, setProductId] = useState<number | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await menuService.getCategories();

      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(
      (e.target as HTMLSelectElement).selectedOptions,
      (option) => parseInt(option.value),
    );

    setFormData({
      ...formData,
      category_ids: selectedOptions,
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError("Product name is required");

        return;
      }

      if (!formData.code.trim()) {
        setError("Product code is required");

        return;
      }

      if (!formData.price.trim() || isNaN(parseFloat(formData.price))) {
        setError("Valid price is required");

        return;
      }

      if (formData.category_ids.length === 0) {
        setError("At least one category must be selected");

        return;
      }

      setSubmitting(true);
      setError("");

      // Convert price to cents for API
      const priceInCents = Math.round(parseFloat(formData.price) * 100);

      const productData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        price: priceInCents,
        category_ids: formData.category_ids,
        is_featured: formData.is_featured,
        unavailable: formData.unavailable,
        image_url: formData.image_url || undefined,
      };

      const response = await menuService.createProduct(productData);

      setProductId(response.id);

      // Navigate to product detail page if successful
      navigate(`/menu/products/${response.id}`);
    } catch (err) {
      console.error("Error creating product:", err);
      setError("Failed to create product");
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/menu/products");
  };

  const handleUploadImage = () => {
    // Implementation for image upload would go here
    // This is a placeholder for the actual implementation

    // For now, set a placeholder URL
    setFormData({
      ...formData,
      image_url: "https://example.com/placeholder.jpg",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleCancel}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">Create New Product</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="flat" onPress={handleCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            isLoading={submitting}
            startContent={<Save size={16} />}
            onPress={handleSubmit}
          >
            Save Product
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 space-y-4">
            <h2 className="text-xl font-semibold">Product Information</h2>

            {error && (
              <div className="bg-danger-50 text-danger p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <Input
                isRequired
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />

              <Input
                isRequired
                description="Unique identifier for this product"
                label="Product Code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
              />

              <Textarea
                label="Description"
                minRows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />

              <Input
                isRequired
                label="Price ($)"
                min="0"
                name="price"
                startContent={
                  <div className="pointer-events-none flex items-center">
                    <span className="text-default-400 text-small">$</span>
                  </div>
                }
                step="0.01"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
              />

              <Select
                isRequired
                label="Categories"
                selectionMode="multiple"
                onChange={handleCategoryChange}
              >
                {categories.map((category) => (
                  <SelectItem key={category.id} textValue={category.name}>
                    {category.name}
                  </SelectItem>
                ))}
              </Select>

              <div className="flex flex-col gap-2">
                <Checkbox
                  isSelected={formData.is_featured}
                  name="is_featured"
                  onValueChange={(checked) =>
                    setFormData({ ...formData, is_featured: checked })
                  }
                >
                  Featured Product
                </Checkbox>

                <Checkbox
                  isSelected={formData.unavailable}
                  name="unavailable"
                  onValueChange={(checked) =>
                    setFormData({ ...formData, unavailable: checked })
                  }
                >
                  Temporarily Unavailable
                </Checkbox>
              </div>
            </div>
          </Card>

          {/* Product Types section - only shown after initial product creation */}
          {productId && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Product Types</h2>
              <ProductTypeManager productId={productId} />
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Product Image</h2>

            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {formData.image_url ? (
                <div className="space-y-2">
                  <img
                    alt="Product"
                    className="max-w-full h-auto mx-auto rounded-md"
                    src={formData.image_url}
                  />
                  <Button
                    color="danger"
                    variant="flat"
                    onPress={() => setFormData({ ...formData, image_url: "" })}
                  >
                    Remove Image
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Image className="w-12 h-12 text-default-300 mx-auto" />
                  <p className="text-default-500">No image uploaded</p>
                  <Button
                    color="primary"
                    variant="flat"
                    onPress={handleUploadImage}
                  >
                    Upload Image
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Actions</h2>

            <div className="space-y-4">
              <Button
                className="w-full"
                color="primary"
                isLoading={submitting}
                startContent={<Save size={16} />}
                onPress={handleSubmit}
              >
                Save Product
              </Button>

              <Button className="w-full" variant="flat" onPress={handleCancel}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
